/// <amd-module name="Checks/Approvals/Web/Components/ApprovalWrapperComponent" />
import * as React from "react";

import { ApprovalExecutionOrder } from "Checks/Approvals/Client/Contracts/Approval";
import { ApprovalsCheckComponent } from "Checks/Approvals/Web/Components/ApprovalsCheckComponent";
import * as Resources from "Checks/Approvals/Web/Resources";
import { CheckAction, ChecksTableCellRenderer, ICheckWrapperComponentProps } from "Checks/Client/Contracts/Contracts";
import { ApprovalCheckConfiguration, CheckConfiguration } from "Checks/Client/Contracts/PipelinesChecks";

import { equals, format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Tooltip } from "VSSUI/TooltipEx";
import { VssPersona } from "VSSUI/VssPersona";

const approvalCheckTypeId: string = "8C6F20A7-A545-4486-9777-F762FAFE0D4D";

export class ApprovalWrapperComponent extends VssComponent<ICheckWrapperComponentProps> {

    public constructor(props: ICheckWrapperComponentProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        if (this.props.checkConfigObject) {
            this._approvalCheckConfigObject = this.props.checkConfigObject as ApprovalCheckConfiguration;
            if (!equals(this._approvalCheckConfigObject.type.id, approvalCheckTypeId, true)) {
                return <div />;
            }
        }

        if (this.props.checkTableCellToRender !== undefined && this.props.checkConfigObject) {
            return this.renderCheckTableCell(this.props.checkTableCellToRender);
        }
        else if (this.props.checkAction === CheckAction.Create) {
            return (
                <ApprovalsCheckComponent
                    requesterCanBeApprover={true}
                    checkAction={this.props.checkAction}
                    checkDefinitionRefId={this.props.checkDefinitionRefId}
                    checkName={this.props.checkName}
                    timeout={60 * 24 * 30}
                    {
                        ...this._getCommonProps()
                    } />
            );
        }
        else if (this.props.checkAction === CheckAction.Edit) {
            return (
                <ApprovalsCheckComponent
                    approvers={this._approvalCheckConfigObject.settings.approvers}
                    minimumApprovers={this._approvalCheckConfigObject.settings.minRequiredApprovers}
                    executionOrder={this._approvalCheckConfigObject.settings.executionOrder}
                    instructions={this._approvalCheckConfigObject.settings.instructions}
                    requesterCanBeApprover={!this._approvalCheckConfigObject.settings.requesterCannotBeApprover}
                    checkDefinitionRefId={this.props.checkDefinitionRefId}
                    checkName={this.props.checkName}
                    checkAction={this.props.checkAction}
                    timeout={this._approvalCheckConfigObject.timeout}
                    {
                        ...this._getCommonProps()
                    }
                />
            );
        }
        return <div />;
    }

    private renderCheckTableCell(checkTableCell: ChecksTableCellRenderer): JSX.Element {
        switch (checkTableCell) {
            case ChecksTableCellRenderer.DisplayName:
                return this._renderDisplayNameCell();
            case ChecksTableCellRenderer.Details:
                return this._renderDetailsCell();
            default:
                return <div />;
        }
    }

    private _renderDisplayNameCell(): JSX.Element {
        let displayName = this._getApprovalCheckDisplayName();
        return (
            <Tooltip text={displayName}>
                <div className="body-m primary-text">
                    {displayName}
                </div>
            </Tooltip>
        );
    }

    private _renderDetailsCell(): JSX.Element {
        let details: React.ReactNode = this._renderAvatars(this._approvalCheckConfigObject.settings.approvers);
        return <span>{details}</span>;
    }

    private _getApprovalCheckDisplayName = (): string => {
        let approval = this._approvalCheckConfigObject.settings;
        if (approval.executionOrder === ApprovalExecutionOrder.InSequence) {
            return Resources.ApprovalInSequence;
        }
        else {
            if (approval.minRequiredApprovers === 0 || approval.minRequiredApprovers === approval.approvers.length) {
                return Resources.DefaultApproval;
            }
            else {
                return approval.minRequiredApprovers === 1 ? Resources.SingleMinApproversApproval : format(Resources.MinApproversApproval, approval.minRequiredApprovers);
            }
        }
    }

    private _renderAvatars = (approvers: IdentityRef[]): React.ReactNode => {
        if (approvers.length === 0) {
            return null;
        }
        let approverList = [...approvers];
        let additionalApproverDisplayText = "";
        if (approverList.length > 4) {
            approverList = approverList.slice(0, 3);
            additionalApproverDisplayText = format(Resources.AdditionalApproverDisplayFormat, (approvers.length - 3).toString());
        }
        return (
            <div className="flex-row flex-center">
                {approverList.map((approver) => {
                    let displayName = approver.displayName;
                    let imageUrl = approver.imageUrl;
                    if (!imageUrl) {
                        imageUrl = getIdentityImageUrl(this.context.pageContext, approver);
                    }
                    return (
                        <VssPersona key={displayName} className={"approver-avatar"} displayName={displayName} imageUrl={imageUrl} size="small" />
                    );
                })
                }
                {approvers.length > 4 &&
                    <div className="avatar-count-display">
                        <span className=" body-s primary-text">{additionalApproverDisplayText}</span>
                    </div>
                }
            </div>
        );
    }

    private _getCommonProps = () => {
        return ({
            resource: this.props.resource,
            disableCreateButton: this.props.disableCreateButton ? this.props.disableCreateButton : (val: boolean) => { },
            updateCheckDataAndEnableSave: this.props.updateCheckDataAndEnableSave ? this.props.updateCheckDataAndEnableSave : (val: CheckConfiguration) => { },
            updateTelemetryData: this.props.updateTelemetryData ? this.props.updateTelemetryData : (val: { [key: string ]: string }) => {}
        });
    }

    private _approvalCheckConfigObject: ApprovalCheckConfiguration;
}

Components.add("approval-check-view", ApprovalWrapperComponent);
